<template>
<div>


  <div ref="markdownBody" class="markdown-body" v-html="stableHtml" @click="handleClick" @mouseenter="isHovered = true" @mouseleave="isHovered = false"></div>
  <span class="markdown-pending" v-html="pendingHtml" @mouseenter="isHovered = true" @mouseleave="isHovered = false"></span>

  </div>
</template>

<script>
import MarkdownIt from 'markdown-it';
import mathjax3 from 'markdown-it-mathjax3';
import hljs from 'highlight.js';
import { showToast } from '@/utils/toast';
import 'highlight.js/styles/atom-one-dark.css';

// Shared instance to avoid recreating it for every component
const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  highlight: (str, lang) => {
    let highlighted = '';
    if (lang && hljs.getLanguage(lang)) {
      try {
        highlighted = hljs.highlight(str, { language: lang, ignoreIllegals: true }).value;
      } catch (__) {
        highlighted = md.utils.escapeHtml(str);
      }
    } else {
      highlighted = md.utils.escapeHtml(str);
    }
    return '<div class="code-block-wrapper"><div class="code-block-actions"><button type="button" class="edit-code-btn" hidden>Edit</button><button type="button" class="copy-code-btn" data-i18n-key="markdownRenderer.copyCode">Copy</button></div><pre class="hljs"><code>' + highlighted + '</code></pre></div>';
  }
}).use(mathjax3, {
  tex: {
    inlineMath: [['$', '$'], ['\\(', '\\)']],
    displayMath: [['$$', '$$'], ['\\[', '\\]']]
  }
});

const createBackslashInlineMathRule = (openMarker, closeMarker, tokenType, display) => {
  return (state, silent) => {
    if (state.src.slice(state.pos, state.pos + openMarker.length) !== openMarker) {
      return false;
    }

    const start = state.pos + openMarker.length;
    const match = state.src.indexOf(closeMarker, start);
    if (match === -1) {
      return false;
    }

    if (!silent) {
      const token = state.push(tokenType, 'math', 0);
      token.markup = openMarker;
      token.content = state.src.slice(start, match);
      token.display = display;
    }

    state.pos = match + closeMarker.length;
    return true;
  };
};

const backslashBlockMathRule = (state, startLine, endLine, silent) => {
  let pos = state.bMarks[startLine] + state.tShift[startLine];
  let max = state.eMarks[startLine];

  if (pos + 2 > max || state.src.slice(pos, pos + 2) !== '\\[') {
    return false;
  }

  if (silent) {
    return true;
  }

  pos += 2;
  let firstLine = state.src.slice(pos, max);
  let lastLine = '';
  let nextLine = startLine;
  let found = false;

  if (firstLine.trim().endsWith('\\]')) {
    firstLine = firstLine.trim().slice(0, -2);
    found = true;
  }

  while (!found) {
    nextLine += 1;
    if (nextLine >= endLine) {
      break;
    }

    pos = state.bMarks[nextLine] + state.tShift[nextLine];
    max = state.eMarks[nextLine];

    if (pos < max && state.tShift[nextLine] < state.blkIndent) {
      break;
    }

    const line = state.src.slice(pos, max);
    const markerIndex = line.lastIndexOf('\\]');
    if (markerIndex !== -1) {
      lastLine = line.slice(0, markerIndex);
      found = true;
    }
  }

  if (!found) {
    return false;
  }

  state.line = nextLine + 1;
  const token = state.push('math_block', 'math', 0);
  token.block = true;
  token.content = (firstLine && firstLine.trim() ? firstLine + '\n' : '') +
    state.getLines(startLine + 1, nextLine, state.tShift[startLine], true) +
    (lastLine && lastLine.trim() ? lastLine : '');
  token.map = [startLine, state.line];
  token.markup = '\\[';
  return true;
};

md.inline.ruler.before('escape', 'math_inline_paren', createBackslashInlineMathRule('\\(', '\\)', 'math_inline', false));
md.inline.ruler.before('escape', 'math_inline_bracket', createBackslashInlineMathRule('\\[', '\\]', 'math_block', true));
md.block.ruler.before('paragraph', 'math_block_bracket', backslashBlockMathRule, {
  alt: ['paragraph', 'reference', 'blockquote', 'list']
});

// Wrap math rules to preserve raw TeX source in data attributes
const wrapMathRule = (rule) => {
  if (!rule) {
    console.warn('Math rule not found to wrap!');
    return rule;
  }
  return (...args) => {
    const [tokens, idx] = args;
    const content = tokens[idx].content;
    const rendered = rule(...args);
    // Inject data-tex attribute into the container
    if (rendered.includes('mjx-container')) {
      return rendered.replace('<mjx-container', `<mjx-container data-tex="${md.utils.escapeHtml(content)}"`);
    } else if (rendered.includes('mjx-chtml')) {
       return rendered.replace('<mjx-chtml', `<mjx-chtml data-tex="${md.utils.escapeHtml(content)}"`);
    }
    return rendered;
  };
};

md.renderer.rules.math_inline = wrapMathRule(md.renderer.rules.math_inline);
md.renderer.rules.math_block = wrapMathRule(md.renderer.rules.math_block);

// Stamp each fenced block with its source line so the edit button can map back to the markdown source
const defaultFenceRender = md.renderer.rules.fence;
md.renderer.rules.fence = function (tokens, idx, options, env, self) {
  const rendered = defaultFenceRender(tokens, idx, options, env, self);
  const map = tokens[idx].map;
  if (!map) return rendered;
  return rendered.replace('<div class="code-block-wrapper">', `<div class="code-block-wrapper" data-fence-line="${map[0]}">`);
};

// Configure link renderer
const defaultLinkRender = md.renderer.rules.link_open || function(tokens, idx, options, env, self) {
  return self.renderToken(tokens, idx, options);
};

md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  const aIndex = tokens[idx].attrIndex('target');
  if (aIndex < 0) {
    tokens[idx].attrPush(['target', '_blank']);
  } else {
    tokens[idx].attrs[aIndex][1] = '_blank';
  }
  const relIndex = tokens[idx].attrIndex('rel');
  if (relIndex < 0) {
    tokens[idx].attrPush(['rel', 'noopener noreferrer']);
  } else {
    tokens[idx].attrs[relIndex][1] = 'noopener noreferrer';
  }
  return defaultLinkRender(tokens, idx, options, env, self);
};

export default {
  name: 'MarkdownRenderer',
  props: {
    content: {
      type: String,
      default: ''
    },
    protectSelection: {
      type: Boolean,
      default: false
    },
    enhanceFormulasOnRender: {
      type: Boolean,
      default: true
    },
    editableCode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      renderTimer: null,
      stableHtml: '',
      lastCommittedSource: '',
      pendingHtml: '',
      needsRender: false,
      isHovered: false,
      selectionListenerAttached: false
    };
  },
  watch: {
    '$i18n.locale'() {
      this.$nextTick(() => {
        this.localizeCodeButtons();
      });
    },
    editableCode() {
      this.$nextTick(() => {
        this.localizeCodeButtons();
      });
    },
    enhanceFormulasOnRender(enabled) {
      if (enabled && this.mayContainFormula(this.content)) {
        this.$nextTick(() => {
          this.enhanceFormulas();
        });
      }
    },
    content: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
            this.stableHtml = '';
            this.lastCommittedSource = '';
            this.pendingHtml = '';
            this.clearTimer();
            return;
        }
        
        // If content reset or non-append update (e.g. history load), full render immediately
        if (newVal.length < this.lastCommittedSource.length || !newVal.startsWith(this.lastCommittedSource)) {
            this.doFullRender(newVal);
            return;
        }

        // Calculate the new part (diff)
        const diff = newVal.slice(this.lastCommittedSource.length);
        if (!diff) return;

        // Simple text escape for the preview (append to HTML)
        const escapedDiff = diff
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\n/g, "<br/>");
        
        // Append to pending view
        this.pendingHtml = escapedDiff;

        // Schedule full render (Throttle)
        if (!this.renderTimer) {
            const delay = this.isHovered ? 3000 : 1000;
            this.renderTimer = setTimeout(() => {
                this.checkAndRender();
            }, delay);
        }
      }
    }
  },
  mounted() {
    this.updateSelectionListener();
    this.localizeCodeButtons();
  },
  beforeDestroy() {
    this.removeSelectionListener();
    this.clearTimer();
  },
  unmounted() {
    this.removeSelectionListener();
    this.clearTimer();
  },
  updated() {
    this.updateSelectionListener();
  },
  methods: {
    updateSelectionListener() {
      if (this.protectSelection && !this.selectionListenerAttached) {
        document.addEventListener('selectionchange', this.handleSelectionChange);
        this.selectionListenerAttached = true;
      } else if (!this.protectSelection && this.selectionListenerAttached) {
        this.removeSelectionListener();
      }
    },
    removeSelectionListener() {
      if (this.selectionListenerAttached) {
        document.removeEventListener('selectionchange', this.handleSelectionChange);
        this.selectionListenerAttached = false;
      }
    },
    hasActiveSelection() {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        return false;
      }
      return selection.toString().length > 0;
    },
    handleSelectionChange() {
      if (this.needsRender && !this.hasActiveSelection()) {
        this.clearTimer();
        this.renderTimer = setTimeout(() => {
          this.checkAndRender();
        }, 200);
      }
    },
    checkAndRender() {
      this.clearTimer();
      if (this.protectSelection && this.hasActiveSelection()) {
        this.needsRender = true;
        const delay = this.isHovered ? 3000 : 1000;
        this.renderTimer = setTimeout(() => {
          this.checkAndRender();
        }, delay);
        return;
      }
      this.needsRender = false;
      this.doFullRender(this.content);
    },
    doFullRender(text) {
      //console.log("doFullRender",text);
        this.clearTimer();
        const html = md.render(text);
        this.stableHtml = html;
        this.lastCommittedSource = text;
        this.pendingHtml = '';
        
        // After rendering, enhance MathJax formulas for preview
        this.$nextTick(() => {
          this.localizeCodeButtons();
          if (this.enhanceFormulasOnRender && this.mayContainFormula(text)) {
            this.enhanceFormulas();
          }
        });
    },
    mayContainFormula(text = '') {
      return text.includes('$') || text.includes('\\(') || text.includes('\\[');
    },
    localizeCodeButtons() {
      const root = this.$refs.markdownBody;
      if (!root) return;

      const label = this.$t ? this.$t('markdownRenderer.copyCode') : 'Copy';
      root.querySelectorAll('.copy-code-btn').forEach(button => {
        button.textContent = label;
        button.setAttribute('aria-label', label);
        button.setAttribute('title', label);
      });

      const editLabel = this.$t ? this.$t('markdownRenderer.editCode') : 'Edit';
      root.querySelectorAll('.edit-code-btn').forEach(button => {
        button.textContent = editLabel;
        button.setAttribute('aria-label', editLabel);
        button.setAttribute('title', editLabel);
        button.hidden = !this.editableCode;
      });
    },
    handleEditCode(wrapper) {
      const fenceLine = Number(wrapper && wrapper.getAttribute('data-fence-line'));
      // Parse the latest content rather than the throttled render: content only grows by appending,
      // so the fence still starts on the same line and this picks up any not-yet-rendered tail.
      const source = this.content;
      if (!wrapper || !wrapper.hasAttribute('data-fence-line') || !Number.isInteger(fenceLine) || !source) return;
      const token = md.parse(source, {}).find(item => item.type === 'fence' && item.map && item.map[0] === fenceLine);
      if (!token) return;
      const info = token.info ? token.info.trim() : '';
      this.$emit('edit-code', {
        code: token.content,
        language: info ? info.split(/\s+/)[0] : '',
        map: token.map.slice(),
        markup: token.markup,
        source
      });
    },
    enhanceFormulas() {
      const formulas = this.$refs.markdownBody ? this.$refs.markdownBody.querySelectorAll('.mjx-chtml, mjx-container') : [];
      if (formulas.length === 0) return;

      for (const formula of formulas) {
        if (formula.dataset.enhanced === 'true') continue;

        const rawLaTeX = this.extractRawLatex(formula);
        formula.dataset.enhanced = 'true';
        formula.classList.add('formula-preview-trigger');
        formula.setAttribute('title', rawLaTeX || 'Formula');
        formula.addEventListener('click', (e) => {
          e.stopPropagation();
          this.$emit('preview-formula', { rawLaTeX });
        });
      }
    },
    extractRawLatex(formula) {
      const annotation = formula.querySelector('annotation[encoding="application/x-tex"]');
      if (annotation) return annotation.textContent.trim();

      const texScript = formula.querySelector('script[type="math/tex"]');
      if (texScript) return texScript.textContent.trim();

      const texHidden = formula.querySelector('.mjx-tex, .MathJax_Preview, .MathJax_TeX_Source');
      if (texHidden) return texHidden.textContent.trim();

      const hiddenTex = formula.querySelector('mjx-assistive-mml annotation[encoding="application/x-tex"]');
      if (hiddenTex) return hiddenTex.textContent.trim();

      if (formula.previousElementSibling && formula.previousElementSibling.tagName === 'SCRIPT' && formula.previousElementSibling.type === 'math/tex') {
        return formula.previousElementSibling.textContent.trim();
      }

      return formula.getAttribute('data-tex') || formula.getAttribute('data-latex') || formula.getAttribute('latex') || '';
    },    clearTimer() {
        if (this.renderTimer) {
            clearTimeout(this.renderTimer);
            this.renderTimer = null;
        }
    },
    async copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (!successful) {
        throw new Error('Copy command failed');
      }
    },
    async handleCopyCode(text, target) {
      try {
        await this.copyText(text);
        const copiedLabel = this.$t('markdownRenderer.copied');
        showToast(copiedLabel, 'success');

        if (target) {
          const originalText = target.textContent;
          target.textContent = copiedLabel;
          clearTimeout(target._copyResetTimer);
          target._copyResetTimer = setTimeout(() => {
            target.textContent = originalText;
            target._copyResetTimer = null;
          }, 1200);
        }
      } catch (err) {
        console.error('Failed to copy code:', err);
        showToast(this.$t('markdownRenderer.copyFailed'), 'error');
      }
    },
    handleClick(e) {
      // Delegate copy code event
      const target = e.target;
      if (target.classList.contains('edit-code-btn')) {
        this.handleEditCode(target.closest('.code-block-wrapper'));
      } else if (target.classList.contains('copy-code-btn')) {
        const wrapper = target.closest('.code-block-wrapper');
        const pre = wrapper ? wrapper.querySelector('pre') : null;
        const codeBlock = pre ? pre.querySelector('code') : null;
        if (codeBlock) {
          const text = codeBlock.innerText || codeBlock.textContent;
          this.handleCopyCode(text, target);
        }
      } else if (target.tagName === 'IMG') {
        // Handle image preview
        const url = target.getAttribute('src');
        if (url) {
          this.$emit('preview-image', url);
        }
      }
    }
  }
};
</script>

<style scoped>
/* Scoped styles if needed, but markdown-body usually relies on global or deep styles */

/* Markdown Cleanups */
.markdown-body {
      word-break: break-all;
  font-family: inherit !important;
  line-height: 1.6 !important;
  background-color: transparent !important;
  color: inherit !important;
}

.markdown-body :deep(.formula-preview-trigger) {
  cursor: pointer;
  max-width: 100%;
}

.markdown-body :deep(mjx-container[display="true"]) {
  overflow-x: auto;
  overflow-y: hidden;
}

.markdown-body :deep(mjx-container svg) {
  max-width: 100%;
}

</style>
